import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert as MuiAlert,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Grid,
  useTheme,
  alpha
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Headset,
  Computer,
  Fullscreen,
  Tab as TabIcon,
  Apps,
  Warning,
  ExpandMore,
  ExpandLess,
  Security,
  Refresh,
  PlayCircle,
  Close,
  Info,
  VerifiedUser,
  Lock
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const PreExamCheck = ({ onReady, onFail }) => {
  const theme = useTheme();
  const [checks, setChecks] = useState({
    browser: false,
    fullscreen: false,
    audio: false,
    tabCount: false,
    security: false
  });

  const [systemInfo, setSystemInfo] = useState({
    warnings: [],
    openTabs: 1,
    securityLevel: 'High'
  });

  const [activeStep, setActiveStep] = useState(0);
  const [expandDetails, setExpandDetails] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'success'
  });

  const showMessage = (msg, type = 'success') => {
    setSnackbar({ open: true, message: msg, type });
  };

  const closeMessage = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Check Browser Compatibility
  useEffect(() => {
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isFirefox = /Firefox/.test(navigator.userAgent);
    const isEdge = /Edg/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

    setChecks(prev => ({ 
      ...prev, 
      browser: isChrome || isFirefox || isEdge || isSafari 
    }));

    if (isChrome) showMessage('✓ Chrome browser detected - Recommended', 'success');
    else if (isFirefox) showMessage('✓ Firefox browser detected - Compatible', 'info');
    else if (isEdge) showMessage('✓ Edge browser detected - Compatible', 'info');
    else if (isSafari) showMessage('⚠ Safari browser detected - Limited support', 'warning');
    else showMessage('⚠ Unsupported browser detected', 'warning');
  }, []);

  // Enhanced Security Check
  const checkSecurity = async () => {
    setScanning(true);
    try {
      const warnings = [];
      let tabCountOk = true;
      let securityScore = 100;

      // Detect Multiple Windows/Tabs using Page Visibility API
      if (document.hidden) {
        warnings.push('Browser window is not in focus - Possible other tabs/windows open');
        securityScore -= 30;
        tabCountOk = false;
      }

      // Check if window is maximized
      if (window.outerWidth < window.screen.availWidth || window.outerHeight < window.screen.availHeight) {
        warnings.push('Window not maximized - Please maximize browser window');
        securityScore -= 10;
      }

      // Detect developer tools
      const devToolsOpen = () => {
        const widthThreshold = window.outerWidth - window.innerWidth > 160;
        const heightThreshold = window.outerHeight - window.innerHeight > 160;
        return widthThreshold || heightThreshold;
      };

      if (devToolsOpen()) {
        warnings.push('Developer tools detected - Please close developer tools');
        securityScore -= 50;
        tabCountOk = false;
      }

      // Detect screen sharing/recording software
      const userAgent = navigator.userAgent.toLowerCase();
      const screenShareKeywords = ['teams', 'zoom', 'discord', 'obs', 'streamlabs', 'screencast', 'record'];
      screenShareKeywords.forEach(keyword => {
        if (userAgent.includes(keyword)) {
          warnings.push(`Screen sharing software detected: ${keyword}`);
          securityScore -= 40;
          tabCountOk = false;
        }
      });

      // Check if in private/incognito mode
      const isPrivateMode = () => {
        return new Promise((resolve) => {
          const db = indexedDB.open('test');
          db.onerror = () => resolve(true);
          db.onsuccess = () => resolve(false);
        });
      };

      const privateMode = await isPrivateMode();
      if (privateMode) {
        warnings.push('Private browsing mode detected - Not recommended for exams');
        securityScore -= 20;
      }

      // Update system info
      setSystemInfo({
        warnings: warnings,
        openTabs: tabCountOk ? 'Single' : 'Multiple',
        securityLevel: securityScore >= 80 ? 'High' : securityScore >= 50 ? 'Medium' : 'Low'
      });

      // Update checks
      setChecks(prev => ({ 
        ...prev, 
        tabCount: tabCountOk,
        security: securityScore >= 70
      }));

      if (warnings.length === 0) {
        showMessage('✓ System clean - No suspicious activity detected', 'success');
      } else {
        showMessage(`⚠ ${warnings.length} issues detected - Please resolve before starting`, 'warning');
      }

    } catch (err) {
      console.error('Security check error:', err);
      showMessage('Security check completed with errors', 'error');
    } finally {
      setScanning(false);
    }
  };

  // Real-time Monitoring
  useEffect(() => {
    // Initial check
    checkSecurity();

    // Check every 3 seconds
    const interval = setInterval(() => {
      checkSecurity();
    }, 3000);

    // Event listeners for real-time detection
    const handleVisibilityChange = () => {
      if (!document.hidden) checkSecurity();
    };

    const handleFocus = () => {
      checkSecurity();
    };

    const handleResize = () => {
      checkSecurity();
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('resize', handleResize);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Test Audio
  const testAudio = async () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const gainNode = context.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(context.destination);
      
      oscillator.frequency.value = 523.25; // C5 note
      oscillator.type = 'sine';
      gainNode.gain.value = 0.1;
      
      oscillator.start();
      
      setTimeout(() => {
        oscillator.stop();
        context.close();
        setChecks(prev => ({ ...prev, audio: true }));
        showMessage('✓ Audio test successful - System audio is working', 'success');
      }, 300);
      
    } catch (err) {
      setChecks(prev => ({ ...prev, audio: false }));
      showMessage('✗ Audio test failed - Please check your speakers/headphones', 'error');
    }
  };

  // Toggle Fullscreen
  const toggleFullscreen = async () => {
    try {
      const elem = document.documentElement;

      if (!document.fullscreenElement) {
        // Enter fullscreen
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) {
          await elem.msRequestFullscreen();
        }
      } else {
        // Exit fullscreen (not recommended during exam)
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }

    } catch (err) {
      console.error('Fullscreen error:', err);
      showMessage('Fullscreen not supported or blocked by browser', 'error');
    }
  };

  // Check Fullscreen Status
  useEffect(() => {
    const checkFullscreenStatus = () => {
      const isFullscreen = document.fullscreenElement || 
                          document.webkitFullscreenElement || 
                          document.msFullscreenElement;
      
      setChecks(prev => ({ ...prev, fullscreen: !!isFullscreen }));
    };

    const events = ['fullscreenchange', 'webkitfullscreenchange', 'msfullscreenchange'];
    events.forEach(event => {
      document.addEventListener(event, checkFullscreenStatus);
    });

    checkFullscreenStatus();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, checkFullscreenStatus);
      });
    };
  }, []);

  // Update active step based on checks
  useEffect(() => {
    let step = 0;
    if (checks.browser) step = 1;
    if (checks.browser && checks.audio) step = 2;
    if (checks.browser && checks.audio && checks.fullscreen) step = 3;
    if (checks.browser && checks.audio && checks.fullscreen && checks.security && checks.tabCount) step = 4;
    setActiveStep(step);
  }, [checks]);

  // Check if all requirements are met
  const allChecked = checks.browser && checks.fullscreen && checks.audio && checks.tabCount && checks.security;

  // Status Icon Component
  const StatusIcon = ({ status, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {status ? (
        <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 24 }} />
      ) : (
        <Cancel sx={{ color: theme.palette.error.main, fontSize: 24 }} />
      )}
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );

  // Steps for the exam setup process
  const steps = ['System Check', 'Audio Test', 'Fullscreen', 'Security Scan', 'Ready'];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a3c6d 0%, #4a90e2 100%)',
        p: 2
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              overflow: 'hidden',
              boxShadow: '0 32px 64px rgba(0,0,0,0.2)',
            }}
          >
            {/* Decorative top bar */}
            <Box
              sx={{
                height: '6px',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f7b731 100%)',
              }}
            />

            <Box sx={{ p: { xs: 3, md: 5 } }}>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                    }}
                  >
                    <Security sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                </motion.div>
                
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  Exam Environment Check
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, fontWeight: 500 }}
                >
                  Ensure your system meets all requirements for a secure exam session
                </Typography>
              </Box>

              {/* Progress Stepper */}
              <Stepper
                activeStep={activeStep}
                sx={{ mb: 5 }}
                alternativeLabel
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* System Status Grid */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatusCard
                    icon={<Computer />}
                    title="Browser"
                    status={checks.browser}
                    actionText="Supported"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatusCard
                    icon={<Headset />}
                    title="Audio"
                    status={checks.audio}
                    actionText="Test Required"
                    onAction={testAudio}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatusCard
                    icon={<Fullscreen />}
                    title="Fullscreen"
                    status={checks.fullscreen}
                    actionText={checks.fullscreen ? "Active" : "Activate"}
                    onAction={toggleFullscreen}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatusCard
                    icon={<Lock />}
                    title="Security"
                    status={checks.security}
                    actionText={systemInfo.securityLevel}
                    statusText={systemInfo.securityLevel}
                  />
                </Grid>
              </Grid>

              {/* Security Scan Section */}
              <Card
                sx={{
                  mb: 4,
                  border: `2px solid ${checks.security ? theme.palette.success.main : theme.palette.error.main}`,
                  borderRadius: 3,
                  background: alpha(checks.security ? theme.palette.success.main : theme.palette.error.main, 0.05),
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700}>
                      Security Scan
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {scanning && (
                        <>
                          <LinearProgress sx={{ width: 100 }} />
                          <Typography variant="caption">Scanning...</Typography>
                        </>
                      )}
                      <Tooltip title="Rescan system">
                        <IconButton onClick={checkSecurity} size="small">
                          <Refresh />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    Real-time monitoring for suspicious activity
                  </Typography>

                  {systemInfo.warnings.length > 0 && (
                    <>
                      <Button
                        fullWidth
                        variant="text"
                        size="small"
                        onClick={() => setExpandDetails(!expandDetails)}
                        endIcon={expandDetails ? <ExpandLess /> : <ExpandMore />}
                        sx={{ mb: 2 }}
                      >
                        {expandDetails ? 'Hide Details' : `Show ${systemInfo.warnings.length} Issues`}
                      </Button>

                      <Collapse in={expandDetails}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            borderColor: theme.palette.warning.main,
                            borderRadius: 2,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={600} color="warning.dark" gutterBottom>
                            ⚠️ Security Issues Detected:
                          </Typography>
                          <List dense>
                            {systemInfo.warnings.map((warning, idx) => (
                              <ListItem key={idx} sx={{ py: 0.5 }}>
                                <Warning sx={{ mr: 1, fontSize: 18, color: 'warning.dark' }} />
                                <ListItemText
                                  primary={warning}
                                  primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Collapse>
                    </>
                  )}

                  {systemInfo.warnings.length === 0 && (
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.success.main}`,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} color="success.dark">
                        ✓ System secure - No suspicious activity detected
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* System Requirements */}
              <Card sx={{ mb: 4, borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    System Requirements
                  </Typography>
                  <List dense>
                    <RequirementItem
                      met={checks.browser}
                      text="Modern browser (Chrome, Firefox, Edge, Safari)"
                    />
                    <RequirementItem
                      met={checks.fullscreen}
                      text="Fullscreen mode enabled"
                    />
                    <RequirementItem
                      met={checks.audio}
                      text="Working audio system"
                    />
                    <RequirementItem
                      met={checks.tabCount}
                      text="No other tabs or applications open"
                    />
                    <RequirementItem
                      met={checks.security}
                      text="Secure environment (no screen sharing/recording)"
                    />
                  </List>
                </CardContent>
              </Card>

              {/* Final Status */}
              <Paper
                sx={{
                  p: 3,
                  mb: 4,
                  borderRadius: 3,
                  bgcolor: allChecked
                    ? alpha(theme.palette.success.main, 0.1)
                    : alpha(theme.palette.warning.main, 0.1),
                  border: `2px solid ${allChecked ? theme.palette.success.main : theme.palette.warning.main}`,
                }}
              >
                <Typography variant="h6" fontWeight={700} gutterBottom textAlign="center">
                  {allChecked ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: 'success.main' }} />
                      All Checks Passed - Ready to Start Exam
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Warning sx={{ color: 'warning.main' }} />
                      System Not Ready - Please Complete All Checks
                    </Box>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {allChecked
                    ? 'Your system meets all requirements for a secure exam session.'
                    : 'Please address all issues above before starting the exam.'}
                </Typography>
              </Paper>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={onFail}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                  }}
                  startIcon={<Close />}
                >
                  Cancel
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  disabled={!allChecked}
                  onClick={onReady}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    background: allChecked
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : theme.palette.grey[300],
                    fontWeight: 700,
                    fontSize: '1rem',
                    '&:disabled': {
                      background: theme.palette.grey[300],
                      color: theme.palette.grey[500],
                    },
                  }}
                  startIcon={allChecked ? <PlayCircle /> : <Warning />}
                >
                  {allChecked ? 'Start Exam' : 'Complete All Checks First'}
                </Button>
              </Box>

              {/* Important Notes */}
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  <strong>Important:</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  • Do not switch tabs or windows during the exam<br />
                  • Keep fullscreen mode activated at all times<br />
                  • Close all other applications before starting<br />
                  • Ensure stable internet connection throughout<br />
                  • Violations may result in exam termination
                </Typography>
              </Box>
            </Box>
          </Paper>
        </motion.div>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert 
          severity={snackbar.type} 
          elevation={6} 
          variant="filled"
          onClose={closeMessage}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

// Sub-components
const StatusCard = ({ icon, title, status, actionText, statusText, onAction }) => (
  <Card
    sx={{
      height: '100%',
      borderRadius: 3,
      background: status ? alpha('#26c281', 0.1) : alpha('#e74c3c', 0.1),
      border: `2px solid ${status ? '#26c281' : '#e74c3c'}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4,
      },
    }}
  >
    <CardContent sx={{ textAlign: 'center', p: 3 }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
          background: status ? alpha('#26c281', 0.2) : alpha('#e74c3c', 0.2),
          color: status ? '#26c281' : '#e74c3c',
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Chip
        label={status ? '✓ Passed' : '✗ Failed'}
        size="small"
        color={status ? 'success' : 'error'}
        sx={{ mb: 1 }}
      />
      {statusText && (
        <Typography variant="caption" color="text.secondary" display="block">
          {statusText}
        </Typography>
      )}
      {onAction && (
        <Button
          variant="outlined"
          size="small"
          onClick={onAction}
          sx={{ mt: 1 }}
          fullWidth
        >
          {actionText}
        </Button>
      )}
    </CardContent>
  </Card>
);

const RequirementItem = ({ met, text }) => (
  <ListItem sx={{ py: 0.5 }}>
    {met ? (
      <CheckCircle sx={{ color: 'success.main', fontSize: 20, mr: 1 }} />
    ) : (
      <Cancel sx={{ color: 'error.main', fontSize: 20, mr: 1 }} />
    )}
    <ListItemText
      primary={text}
      primaryTypographyProps={{
        fontSize: '0.9rem',
        color: met ? 'text.primary' : 'text.secondary',
      }}
    />
  </ListItem>
);

export default PreExamCheck;
